import React from "react";
import { FaCalendarAlt, FaFlag, FaSchool } from "react-icons/fa";
import GenderIcon from "../../../../components/GenderIcon";
import { getChildFullName, getChildInitials } from "../../shared/utils/childIdentity";

export default function ChildDetailsHeader({
  child,
  placement,
  withdrawnDateValue,
  withdrawnMessage,
  currentClassroomName,
  futureClassroomName,
  currentClassroom,
  futureClassroom,
  schoolYear,
  startDate,
  labels,
}) {
  const displayClassroom = futureClassroom ? futureClassroomName : currentClassroom ? currentClassroomName : labels.noClassroom;
  const showPlacementMeta = schoolYear || startDate;

  return (
    <header
      className={`relative overflow-hidden rounded-[28px] border p-7 shadow-[0_18px_45px_rgba(67,56,202,0.13)] backdrop-blur sm:p-8 ${
        placement.tone === "rose"
          ? "border-rose-100/80 bg-gradient-to-r from-rose-50 via-white to-rose-50/50 dark:border-rose-400/30 dark:from-rose-950/50 dark:via-slate-950 dark:to-rose-950/20"
          : "border-indigo-100/80 bg-gradient-to-r from-indigo-50 via-white to-sky-50/80 dark:border-indigo-400/25 dark:from-indigo-950/45 dark:via-slate-950 dark:to-sky-950/25"
      }`}
    >
      <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[42%] opacity-75 lg:block">
        <div className="absolute bottom-4 right-6 h-20 w-36 rounded-t-full bg-emerald-100/70 dark:bg-emerald-300/10" />
        <div className="absolute bottom-4 right-28 h-28 w-44 rounded-t-full bg-sky-100/80 dark:bg-sky-300/10" />
        <div className="absolute bottom-6 right-44 h-24 w-12 rounded-full bg-indigo-200/60 dark:bg-indigo-300/15" />
        <div className="absolute bottom-5 right-56 h-16 w-10 rounded-full bg-violet-200/60 dark:bg-violet-300/15" />
        <div className="absolute bottom-3 right-8 h-28 w-20 rounded-t-[28px] bg-indigo-300/45 dark:bg-indigo-300/15">
          <div className="mx-auto mt-5 h-4 w-4 rounded-full bg-white/70 dark:bg-white/20" />
          <div className="mx-auto mt-12 h-7 w-8 rounded-t-full bg-white/70 dark:bg-white/15" />
        </div>
        <div className="absolute right-20 top-12 h-4 w-14 rounded-full bg-white/80 dark:bg-white/10" />
        <div className="absolute right-52 top-20 h-5 w-20 rounded-full bg-white/80 dark:bg-white/10" />
      </div>

      <div className="relative flex flex-col gap-6 md:flex-row md:items-center">
        <div className="relative shrink-0">
          <div className="flex h-36 w-36 items-center justify-center rounded-full border-[6px] border-white bg-indigo-50 text-4xl font-bold uppercase text-indigo-700 shadow-xl shadow-indigo-900/10 dark:border-white/20 dark:bg-indigo-400/15 dark:text-indigo-100 sm:h-40 sm:w-40">
            <span>{getChildInitials(child) || "?"}</span>
          </div>
          <span className="absolute bottom-3 right-2 flex h-12 w-12 items-center justify-center rounded-2xl border-4 border-white bg-white text-2xl shadow-lg dark:border-slate-950 dark:bg-slate-900">
            <GenderIcon gender={child.gender} />
          </span>
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="min-w-0 break-words text-4xl font-bold text-slate-950 dark:text-white">
              {getChildFullName(child)}
            </h2>
            <span className="text-3xl">
              <GenderIcon gender={child.gender} />
            </span>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 dark:bg-indigo-500">
              <FaCalendarAlt aria-hidden="true" />
              {futureClassroom ? labels.planned : placement.label}
            </span>
            <span className="text-sm font-semibold text-slate-500 dark:text-slate-300">
              {futureClassroom ? labels.futurePlacement : placement.helper}
            </span>
            {withdrawnDateValue && (
              <span className="rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-700 dark:bg-rose-500/15 dark:text-rose-100">
                {withdrawnDateValue}
              </span>
            )}
          </div>

          <div className="mt-6 flex items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-indigo-100 bg-white text-2xl text-indigo-600 shadow-sm dark:border-white/10 dark:bg-white/10 dark:text-indigo-200">
              <FaSchool aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className="break-words text-xl font-bold text-slate-950 dark:text-white">{displayClassroom}</p>
              {showPlacementMeta && (
                <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-300">
                  {schoolYear && (
                    <span>
                      {labels.schoolYear} <strong className="font-bold text-slate-800 dark:text-slate-100">{schoolYear}</strong>
                    </span>
                  )}
                  {schoolYear && startDate ? <span className="mx-2">·</span> : null}
                  {startDate}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {placement.tone === "rose" && (
        <div className="relative mt-5 rounded-2xl border border-rose-100/80 bg-rose-50/80 p-3 text-sm text-rose-800 dark:border-rose-500/50 dark:bg-rose-900/30 dark:text-rose-100">
          {withdrawnMessage}
          {withdrawnDateValue ? ` (${withdrawnDateValue})` : ""}
        </div>
      )}
      <FaFlag className="pointer-events-none absolute right-20 top-[106px] hidden text-indigo-300/60 lg:block" aria-hidden="true" />
    </header>
  );
}
